/**
 * GuardianConsentBanner - shown to an under-18 whose parent has not confirmed
 * ---------------------------------------------------------------------------
 * Sits at the top of the student dashboard. Explains why they cannot apply
 * yet, and lets them resend the email or correct a mistyped address - the two
 * things that actually go wrong in practice.
 *
 * It also covers the second case: a parent who DID agree, but to wording the
 * site has since changed. That reads very differently to the student - their
 * parent said yes and the site is now saying no - so it gets its own sentence
 * rather than the "still waiting" one, which would simply be untrue.
 */

import { useState } from 'react';
import { ShieldAlert, Mail, CheckCircle2, Clock } from 'lucide-react';

import { consentApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';

import { useI18n } from '../hooks/useI18n';
import { useResendCooldown } from '../hooks/useResendCooldown';

export default function GuardianConsentBanner() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();

  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(user.guardian?.email || '');
  const [name, setName] = useState(user.guardian?.name || '');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * The wait applies to "send it again" and NOT to sending to a corrected
   * address.
   *
   * A mistyped parental email is the one failure on this screen that a person
   * can actually fix themselves, and it is the one where every minute counts -
   * their child cannot apply for work until the parent answers. Making them
   * sit through two minutes to correct a typo would be punishing exactly the
   * case worth encouraging.
   */
  const cooldown = useResendCooldown(`consent.${user?._id || 'anon'}`);

  // Only under-18s who are still waiting see this. `guardianConsentGiven` is
  // the server's own answer, so a family whose consent has gone out of date
  // is caught here too - checking consentedAt by hand used to hide the banner
  // from exactly the people who needed it.
  if (!user || user.role !== 'student') return null;
  // The server's answer, not "are they 18?". Since 17 September 2026 age moves
  // on by itself, and whether an eighteenth birthday ends the need for a
  // parent depends on how the account was made - backend/models/Student.js.
  // Asking the age here would hide this banner from a student who is still
  // blocked, which is the one thing it exists to prevent.
  if (user.requiresGuardianConsent === false) return null;
  if (user.guardianConsentGiven) return null;

  const renewal = Boolean(user.guardianConsentOutOfDate);

  const resend = async () => {
    setBusy(true);
    setError('');
    setSent(false);
    try {
      await consentApi.resend(editing ? email : undefined, editing ? name : undefined);
      setSent(true);
      // After the send worked, never before - a failed send must not lock
      // somebody out of trying again.
      cooldown.start();
      setEditing(false);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="card"
      style={{ borderColor: 'var(--color-amber)', borderWidth: 2, marginBottom: 'var(--space-md)' }}
    >
      <div className="row" style={{ gap: 'var(--space-xs)', alignItems: 'flex-start' }}>
        <ShieldAlert size={24} className="text-amber" style={{ flexShrink: 0 }} aria-hidden="true" />

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ marginBottom: 6 }}>
            {renewal ? t('guardianBanner.titleRenewal') : t('guardianBanner.titleWaiting')}
          </h3>

          <p className="text-muted">
            {renewal ? (
              <>
                {t('guardianBanner.renewalBefore')}{' '}
                <strong>{user.guardian?.email || t('guardianBanner.yourGuardian')}</strong>{' '}
                {t('guardianBanner.renewalAfter')}
              </>
            ) : (
              <>
                {t('guardianBanner.waitingBefore')}{' '}
                <strong>{user.guardian?.email || t('guardianBanner.yourGuardian')}</strong>{' '}
                {t('guardianBanner.waitingAfter')}
              </>
            )}
          </p>

          {/* Said the same way as on the confirmation banner next door, on
              purpose. These two screens are the same situation from the
              person's side - something is unfinished and an email is what
              finishes it - so they should not report success differently. */}
          {sent && (
            <div className="alert alert-success" style={{ marginTop: 'var(--space-sm)' }}>
              <CheckCircle2 size={18} aria-hidden="true" />
              <div>
                <strong>{t('resend.sentTitle')}</strong>
                <br />
                <span className="text-sm">
                  {t('resend.sentBody', { email: user.guardian?.email || email })}
                </span>
              </div>
            </div>
          )}
          {error && (
            <div className="alert alert-error" style={{ marginTop: 'var(--space-sm)' }}>
              {error}
            </div>
          )}

          {editing ? (
            <div className="form" style={{ marginTop: 'var(--space-sm)' }}>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="g-name">{t('guardianBanner.theirName')}</label>
                  <input id="g-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="g-email">{t('guardianBanner.theirEmail')}</label>
                  <input id="g-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>

              <div className="row" style={{ gap: 'var(--space-xs)' }}>
                <button type="button" className="btn btn-primary btn-sm btn-inline" onClick={resend} disabled={busy}>
                  <Mail size={16} />
                  {busy ? t('confirmBanner.sending') : t('guardianBanner.saveAndSend')}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-inline"
                  onClick={() => setEditing(false)}
                  disabled={busy}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-sm)' }}>
              <button
                type="button"
                className="btn btn-outline btn-sm btn-inline"
                onClick={resend}
                disabled={busy || !cooldown.ready}
              >
                {cooldown.ready ? <Mail size={16} /> : <Clock size={16} />}
                {busy
                  ? t('confirmBanner.sending')
                  : cooldown.ready
                    ? t('guardianBanner.resend')
                    : t('resend.waitFor', { time: cooldown.label })}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-inline"
                onClick={() => setEditing(true)}
                disabled={busy}
              >
                {t('guardianBanner.wrongAddress')}
              </button>
            </div>
          )}

          <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
            {t('guardianBanner.spamHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
